import { Card, Col, message, Row, Space } from 'antd';
import { get, groupBy, includes, isArray, keys, map } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import api from '../../../common/api';
import {
  BREAKPOINTS,
  DASHBOARD_API_ROUTES,
  DEFAULTDATETIMEFORMATFORREPORTS,
  STAGE_STATUS,
} from '../../../common/constants';
import CommonCard from '../../../components/CommonCard';
import CommonDropdown from '../../../components/CommonDropdown';
import CommonTable from '../../../components/CommonTable';
import { GET_DASHBOARD_USERS_DROPDOWN_LIST } from '../graphql/Queries';
import ChartRenderer from './ChartRenderer';
import CustomRangePicker from './CustomRangePicker';

const UserAnalysis = () => {
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [sortedInfo, setSortedInfo] = useState({});
  const [filterOption, setFilterOption] = useState([
    moment().subtract(1, 'year').format(DEFAULTDATETIMEFORMATFORREPORTS),
    moment().format(DEFAULTDATETIMEFORMATFORREPORTS),
  ]);
  const [userId, setUserId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const getUsers = async () => {
    setLoading(true);
    const eqcTypeId = 'ProjectEqcType.id';
    const eqcTypeName = 'ProjectEqcType.name';
    const projectName = 'Project.name';
    const status = 'EqcStageHistory.status';
    const count = 'EqcStageHistory.count';
    let query = `${DASHBOARD_API_ROUTES.USER_WISE_ANALYSIS}?userId=${userId}`;
    if (isArray(filterOption) && filterOption?.length === 2) {
      query = `${query}&startDate=${filterOption?.[0]}&endDate=${filterOption?.[1]}`;
    }
    api
      .get(query)
      .then((resultSet) => {
        const rawData = get(resultSet, 'data.data', []);
        const eqcData = [];

        const groupedData = groupBy(rawData, (item) => {
          return `${item[projectName]}_${item[eqcTypeName]}`;
        });
        map(keys(groupedData), (groupKey) => {
          const groupItems = groupedData[groupKey];
          const firstItem = groupItems[0];

          let pass = 0;
          let fail = 0;
          let total = 0;
          map(groupItems, (item) => {
            const itemCount = Number(item[count]);

            if (item[status] === STAGE_STATUS.FAIL) {
              fail += itemCount;
            }
            if (item[status] === STAGE_STATUS.PASS) {
              pass += itemCount;
            }
            if (
              includes(
                [
                  STAGE_STATUS.APPROVAL_PENDING,
                  STAGE_STATUS.APPROVED,
                  STAGE_STATUS.BYPASS,
                  STAGE_STATUS.FAIL,
                  STAGE_STATUS.PASS,
                  STAGE_STATUS.REDO,
                  STAGE_STATUS.SKIP,
                ],
                item[status],
              )
            ) {
              total += itemCount;
            }
          });
          const row = {
            key: `${firstItem[projectName]}_${firstItem[eqcTypeName]}`,
            id: firstItem[eqcTypeId],
            projectName: firstItem[projectName],
            eqcTypeName: firstItem[eqcTypeName],
            failCount: Number(fail),
            passCount: Number(pass),
            totalCount: Number(total),
          };

          if (row.id !== null) {
            eqcData.push(row);
          }
        });

        setTableData(eqcData);
      })
      .catch((error) => {
        if (error?.response?.data?.error?.message) {
          message.destroy();
          message.error(error?.response?.data?.error?.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const getUserGraph = async () => {
    let query = `${DASHBOARD_API_ROUTES.USER_WISE_ANALYSIS_GRAPH}?userId=${userId}`;
    if (isArray(filterOption) && filterOption?.length === 2) {
      query = `${query}&startDate=${filterOption?.[0]}&endDate=${filterOption?.[1]}`;
    }
    api
      .get(query)
      .then((resultSet) => {
        setGraphData(get(resultSet, 'data.data'));
      })
      .catch((error) => {
        if (error?.response?.data?.error?.message) {
          message.destroy();
          message.error(error?.response?.data?.error?.message);
        }
      });
  };
  const handleTableChange = (pagination, filter, sorter) => {
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
  };

  const columns = [
    {
      title: 'Project',
      dataIndex: 'projectName',
      key: 'projectName',
      sorter: (a, b) => a?.projectName?.localeCompare(b?.projectName),
      sortOrder: sortedInfo?.columnKey === 'projectName' && sortedInfo?.order,
    },
    {
      title: 'Checklist',
      dataIndex: 'eqcTypeName',
      key: 'eqcTypeName',
      sorter: (a, b) => a?.eqcTypeName?.localeCompare(b?.eqcTypeName),
      sortOrder: sortedInfo?.columnKey === 'eqcTypeName' && sortedInfo?.order,
    },
    {
      title: 'Stage Inspected',
      dataIndex: 'totalCount',
      key: 'totalCount',
      sorter: (a, b) => a?.totalCount - b?.totalCount,
      sortOrder: sortedInfo?.columnKey === 'totalCount' && sortedInfo?.order,
    },
    {
      title: 'Stage Failed',
      dataIndex: 'failCount',
      key: 'failCount',
      sorter: (a, b) => a?.failCount - b?.failCount,
      sortOrder: sortedInfo?.columnKey === 'failCount' && sortedInfo?.order,
      render: (failCount, { totalCount }) =>
        `${failCount} (${parseInt((failCount / totalCount) * 100, 10) || 0}%)`,
    },
  ];

  useEffect(() => {
    if (userId) {
      getUsers();
      getUserGraph();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, filterOption]);

  const FilterView = useCallback(
    ({ userId: uId, currentFilter }) => {
      const handleChange = (value) => {
        setUserId(value);
        setTableData([]);
      };

      const callback = (res) => {
        if (!uId && res?.dashboardProjectUserList?.data?.length) {
          setUserId(res?.dashboardProjectUserList?.data?.[0]?.id);
        }
      };

      return isDesktopViewport ? (
        <Space size={16}>
          <div className="filter-wrapper">
            <CommonDropdown
              placeholder="Select User"
              className="width-200"
              value={uId}
              onChange={handleChange}
              query={GET_DASHBOARD_USERS_DROPDOWN_LIST}
              responsePath="dashboardProjectUserList.data"
              valuePath="id"
              labelPath="name"
              optionKey="user-analysis"
              callback={callback}
              showSearch
              optionFilterProp="children"
              fetchPolicy="network-only"
            />
          </div>
          <CustomRangePicker
            setDateSelected={(date) => {
              setTableData([]);
              if (date?.length > 0) {
                setFilterOption([
                  moment(date?.[0]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                  moment(date?.[1]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                ]);
              } else {
                setFilterOption('');
              }
            }}
            selectedDates={currentFilter}
          />
        </Space>
      ) : (
        <>
          <div className="filter-wrapper">
            <CommonDropdown
              placeholder="Select User"
              className="width-percent-100 mb-15"
              value={uId}
              onChange={handleChange}
              query={GET_DASHBOARD_USERS_DROPDOWN_LIST}
              responsePath="dashboardProjectUserList.data"
              valuePath="id"
              labelPath="name"
              optionKey="user-analysis"
              callback={callback}
              showSearch
              optionFilterProp="children"
              fetchPolicy="network-only"
            />
          </div>
          <CustomRangePicker
            className="width-percent-100"
            setDateSelected={(date) => {
              setTableData([]);
              if (date?.length > 0) {
                setFilterOption([
                  moment(date?.[0]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                  moment(date?.[1]).format(DEFAULTDATETIMEFORMATFORREPORTS),
                ]);
              } else {
                setFilterOption('');
              }
            }}
            selectedDates={currentFilter}
          />
        </>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return isDesktopViewport ? (
    <Card>
      <div className="d-flex align-center justify-between mb-15">
        <h1>User Analysis</h1>
        <FilterView userId={userId} currentFilter={filterOption} />
      </div>
      <Row>
        <Col span={14}>
          <CommonTable
            columns={columns}
            data={tableData}
            onChange={handleTableChange}
            paginationConfig={{ ...paginationProp, total: tableData?.length }}
            rowKey={(obj) => obj?.key}
            loading={loading}
          />
        </Col>
        <Col span={9} offset={1}>
          {graphData?.length ? <ChartRenderer resultSet={graphData} /> : null}
        </Col>
      </Row>
    </Card>
  ) : (
    <div className="common-card-wrapper">
      <div className="mb-15">
        <FilterView userId={userId} currentFilter={filterOption} />
      </div>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <CommonTable
            columns={columns}
            data={tableData}
            onChange={handleTableChange}
            paginationConfig={{ ...paginationProp, total: tableData?.length }}
            rowKey={(obj) => obj?.key}
            loading={loading}
          />
        </Col>
        {graphData?.length ? (
          <Col span={24}>
            <CommonCard className="width-percent-100">
              <ChartRenderer resultSet={graphData} />
            </CommonCard>
          </Col>
        ) : null}
      </Row>
    </div>
  );
};

export default UserAnalysis;
